import { brand } from "../../config/theme";
import { Header } from "../../components/base/Header";
import Sidebar from "./components/Sidebar";
import UnitsTableDock from "./components/UnitsTable/UnitsTableDock";
import MobileBottomBar from "./components/Sidebar/MobileBottomBar";
import {
  AppsSidebar,
  useFilteredCategories,
} from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import { Outlet } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { useVehiclesSubscription } from "../../hooks/useVehiclesSubscription";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useCompanyIntegrators } from "../../features/integradores/queries";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import MapControlsBar from "./components/MapControlsBar";
import DriveViewHost from "../../features/driveView/DriveViewHost";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import {
  useIsSocketReseller,
  useFilteredSocketVehicles,
  useSocketVehicleStats,
  useCompanyFilteredSocketVehicles,
  useSocketConnectionStatus,
} from "../../store/admin/vehiclesSocketSelector";
import { useGroupsStore } from "../../store/admin/groupsStore";
import { useAllGroups, useStatuses } from "../../features/catalogs/queries";
import { useTranslation } from "react-i18next";
import React, { useCallback } from "react";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { requestFCMToken } from "../../firebaseMessaging";
import { logoutUser } from "../../pages/login/functions";

// Imports para modales de POI y Geocerca
import { usePointOfInterestForm } from "../admin/components/sections/pointOfInterest/hooks/usePointOfInterestForm";
import { PointOfInterestModal } from "../admin/components/sections/pointOfInterest/components/PointOfInterestModal";
import { useGeofenceForm } from "../admin/components/sections/geofences/hooks/useGeofenceForm";
import { GeofenceModal } from "../admin/components/sections/geofences/components/GeofenceModal";
import { usePointOfInterestStore } from "../../store/admin/pointOfInterest";
import { createGeofence, useGeofencesStore } from "../../store/admin/geofencesStore";
import { useAlert } from "../../context/AlertContext";
import { resetLastProcessedCoordinates } from "../../components/base/MapPicker";
import type { ThemeClasses } from "../admin/components/sections/geofences/types";
import { useForm } from "react-hook-form";
import {
  TrackingLinkModal,
  type TrackingLinkFormData,
} from "../admin/components/sections/trackingLinks/components";
import { TrackingLinkDetailsModal } from "../admin/components/sections/trackingLinks/components/TrackingLinkDetailsModal";
import { useTrackingLinksStore } from "../../store/admin/trackingLinksStore";
import { useTrackingLinkModalStore } from "../../store/admin/trackingLinkModalStore";

type MapTypeId = "roadmap" | "satellite" | "hybrid" | "terrain";

interface LayoutContextType {
  showGrid: boolean;
  toggleGrid: () => void;
  mapType: MapTypeId | null;
  setMapType: (type: MapTypeId) => void;
  showTraffic: boolean;
  setShowTraffic: (show: boolean) => void;
  showTransit: boolean;
  setShowTransit: (show: boolean) => void;
  showBicycling: boolean;
  setShowBicycling: (show: boolean) => void;
  showGeofences: boolean;
  setShowGeofences: (show: boolean) => void;
  geofenceFilter: "all" | "linear" | "polygonal" | "circular";
  setGeofenceFilter: (
    filter: "all" | "linear" | "polygonal" | "circular",
  ) => void;
  showInterestPoints: boolean;
  setShowInterestPoints: (show: boolean) => void;
  showRoutes: boolean;
  setShowRoutes: (show: boolean) => void;
  showRuler: boolean;
  setShowRuler: (show: boolean) => void;
  showIcons: boolean;
  setShowIcons: (show: boolean) => void;
  showVehicleLabels: boolean;
  setShowVehicleLabels: (show: boolean) => void;
  showSearchBox: boolean;
  setShowSearchBox: (show: boolean) => void;
}

export const LayoutContext = React.createContext<LayoutContextType | null>(
  null,
);

const MOBILE_BREAKPOINT = 768;

const VEHICLE_LABELS_KEY = "dumax.map.vehicleLabels";

const LiteLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const filteredCategories = useFilteredCategories();
  const { t } = useTranslation(["lite", "common"]);

  // Responsive state
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }
    return false;
  });

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set active app for dynamic header title/subtitle and icon
  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "lite",
      t("common:apps.lite.name"),
      t("common:apps.lite.description"),
      faMapMarkerAlt,
      "bg-primary-500",
    );
  }, [setActiveApp, t]);

  // Sincronizar companyUser y companyId desde localStorage
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const setCompanyUser = useCompaniesStore((state) => state.setCompanyUser);
  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const { companies, allCompanies, fetchAllCompanies } = useCompaniesStore();

  // Company selector state
  const isReseller = useIsSocketReseller();
  const selectedCompany = useVehiclesSocketStore(
    (state) => state.selectedCompany,
  );
  const setSelectedCompany = useVehiclesSocketStore(
    (state) => state.setSelectedCompany,
  );
  const selectedStatus = useVehiclesSocketStore(
    (state) => state.selectedStatus,
  );
  const setSelectedStatus = useVehiclesSocketStore(
    (state) => state.setSelectedStatus,
  );

  // Vehicle data for mobile bottom bar
  const vehicles = useFilteredSocketVehicles();
  const unitStats = useSocketVehicleStats();
  const companyFilteredVehicles = useCompanyFilteredSocketVehicles();
  const {
    loading: vehiclesLoading,
    isConnected,
    awaitingCompanySelection,
  } = useSocketConnectionStatus();

  // Search functionality
  const allVehicles = useVehiclesSocketStore((state) => state.vehicles);
  const searchQuery = useVehiclesSocketStore((state) => state.searchQuery);
  const setSearchQuery = useVehiclesSocketStore(
    (state) => state.setSearchQuery,
  );
  const clearSearch = useVehiclesSocketStore((state) => state.clearSearch);

  // Groups data
  const selectedGroup = useVehiclesSocketStore((state) => state.selectedGroup);
  const setSelectedGroup = useVehiclesSocketStore(
    (state) => state.setSelectedGroup,
  );
  const groupList = useGroupsStore((state) => state.groups);
  useAllGroups();
  useStatuses();

  // Computed groups for mobile bottom bar
  const filteredGroupList = selectedCompany
    ? groupList.filter((group) => group.company_id === selectedCompany)
    : groupList;

  const groupStats = filteredGroupList.reduce(
    (acc, group) => {
      acc[group.id] = companyFilteredVehicles.filter((v) =>
        group.devices_ids?.includes(v.id),
      ).length;
      return acc;
    },
    {} as Record<string, number>,
  );

  const groups = useMemo(
    () => [
      {
        id: "all",
        name: t("groups.allGroups"),
        count: companyFilteredVehicles.length,
      },
      ...filteredGroupList.map((group) => ({
        id: group.id,
        name: group.name,
        count: groupStats[group.id] || 0,
      })),
    ],
    [filteredGroupList, groupStats, companyFilteredVehicles.length, t],
  );

  const handleStatusClick = (status: string) => {
    setSelectedStatus(selectedStatus === status ? null : status);
  };

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const { themeMode, isDarkMode, setTheme } = useTheme();

  // Theme classes for mobile bottom bar
  const mobileThemeClasses = useMemo(
    () => ({
      bg: isDarkMode ? "bg-dark-surface" : "bg-white",
      border: isDarkMode ? "border-dark-border" : "border-gray-200",
      text: isDarkMode ? "text-white" : "text-gray-900",
      textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
      cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
      cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
      buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    }),
    [isDarkMode],
  );
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showGrid, setShowGrid] = useState(false); // Grid visible by default
  const [mapType, setMapType] = useState<MapTypeId | null>(null);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showTransit, setShowTransit] = useState(false);
  const [showBicycling, setShowBicycling] = useState(false);
  const [showGeofences, setShowGeofences] = useState(false);
  const [geofenceFilter, setGeofenceFilter] = useState<
    "all" | "linear" | "polygonal" | "circular"
  >("all");
  const [showInterestPoints, setShowInterestPoints] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [showRuler, setShowRuler] = useState(false);
  const [showIcons, setShowIcons] = useState(true);
  // Nombres de las unidades en el mapa. Encendidos por omisión, como en la
  // plataforma anterior; quien los apaga (flotas grandes en una ciudad) lo
  // hace una vez y se recuerda en este navegador.
  const [showVehicleLabels, setShowVehicleLabelsState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(VEHICLE_LABELS_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const setShowVehicleLabels = (show: boolean) => {
    setShowVehicleLabelsState(show);
    try {
      localStorage.setItem(VEHICLE_LABELS_KEY, show ? "1" : "0");
    } catch {
      /* sin almacenamiento: vale para esta sesión */
    }
  };
  const [showSearchBox, setShowSearchBox] = useState(false);

  // Estados para modales de POI y Geocerca
  const [showPOIModal, setShowPOIModal] = useState(false);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [loadingPOISubmit, setLoadingPOISubmit] = useState(false);
  const [loadingGeofenceSubmit, setLoadingGeofenceSubmit] = useState(false);
  const [loadingTrackingLinkSubmit, setLoadingTrackingLinkSubmit] =
    useState(false);

  const { showAlert } = useAlert();
  const { createPointOfInterest, fetchPointsOfInterest } =
    usePointOfInterestStore();

  // Tracking Link Modal state from store
  const trackingLinkModalOpen = useTrackingLinkModalStore(
    (state) => state.isOpen,
  );
  const trackingLinkSelectedVehicle = useTrackingLinkModalStore(
    (state) => state.selectedVehicle,
  );
  const closeTrackingLinkModal = useTrackingLinkModalStore(
    (state) => state.closeModal,
  );
  const openTrackingLinkModal = useTrackingLinkModalStore(
    (state) => state.openModalWithoutVehicle,
  );
  // Tracking Link Details Modal state
  const trackingLinkDetailsOpen = useTrackingLinkModalStore(
    (state) => state.isDetailsOpen,
  );
  const selectedTrackingLink = useTrackingLinkModalStore(
    (state) => state.selectedTrackingLink,
  );
  const closeTrackingLinkDetailsModal = useTrackingLinkModalStore(
    (state) => state.closeDetailsModal,
  );

  const { createTrackingLink, fetchTrackingLinks } = useTrackingLinksStore();

  // Fetch tracking links on mount
  useEffect(() => {
    fetchTrackingLinks({ limit: 100 });
  }, [fetchTrackingLinks]);

  // Form for tracking link
  const trackingLinkForm = useForm<TrackingLinkFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      internalNotes: "",
      units: [],
      unitSelection: "specific",
      accessType: "public",
      accessCode: "",
      activationType: "immediate",
      activationDate: "",
      durationValue: 24,
      durationUnit: "hours",
    },
  });

  // Reset form when modal opens with or without selected vehicle
  useEffect(() => {
    if (trackingLinkModalOpen) {
      if (trackingLinkSelectedVehicle) {
        trackingLinkForm.reset({
          name: `Seguimiento - ${trackingLinkSelectedVehicle.vehicle?.name || trackingLinkSelectedVehicle.imei || trackingLinkSelectedVehicle.id}`,
          internalNotes: "",
          units: [trackingLinkSelectedVehicle.id],
          unitSelection: "specific",
          accessType: "public",
          accessCode: "",
          activationType: "immediate",
          activationDate: "",
          durationValue: 24,
          durationUnit: "hours",
        });
      } else {
        trackingLinkForm.reset({
          name: "",
          internalNotes: "",
          units: [],
          unitSelection: "none",
          accessType: "public",
          accessCode: "",
          activationType: "immediate",
          activationDate: "",
          durationValue: 24,
          durationUnit: "hours",
        });
      }
    }
  }, [trackingLinkModalOpen, trackingLinkSelectedVehicle, trackingLinkForm]);

  // Handler for creating tracking link
  const handleCreateTrackingLink = useCallback(
    async (data: TrackingLinkFormData) => {
      setLoadingTrackingLinkSubmit(true);

      const currentCompanyId =
        companyId || localStorage.getItem("companyId") || "";

      if (!currentCompanyId) {
        showAlert({
          title: t("general.error", { ns: "common" }),
          text: "No se ha seleccionado una compañía",
          icon: "error",
          confirmButtonText: t("general.accept", { ns: "common" }),
          confirmButtonColor: brand.primary,
        });
        setLoadingTrackingLinkSubmit(false);
        return;
      }

      const result = await createTrackingLink({
        name: data.name,
        internalNotes: data.internalNotes,
        units: data.units,
        accessType: data.accessType,
        accessCode:
          data.accessType === "protected" ? data.accessCode : undefined,
        activationType: data.activationType,
        activationDate:
          data.activationType === "scheduled" ? data.activationDate : undefined,
        durationValue: data.durationValue,
        durationUnit: data.durationUnit,
        company_id: currentCompanyId,
      });

      if (result) {
        // Refetch to ensure the list is fully up to date
        fetchTrackingLinks({ limit: 100 });
        showAlert({
          title: t("general.success", { ns: "common" }),
          text: "Link de seguimiento creado correctamente",
          icon: "success",
          confirmButtonText: t("general.accept", { ns: "common" }),
          confirmButtonColor: brand.primary,
        });
        closeTrackingLinkModal();
        trackingLinkForm.reset();
      } else {
        showAlert({
          title: t("general.error", { ns: "common" }),
          text: "Error al crear el link de seguimiento",
          icon: "error",
          confirmButtonText: t("general.accept", { ns: "common" }),
          confirmButtonColor: brand.primary,
        });
      }

      setLoadingTrackingLinkSubmit(false);
    },
    [
      companyId,
      createTrackingLink,
      fetchTrackingLinks,
      showAlert,
      t,
      closeTrackingLinkModal,
      trackingLinkForm,
    ],
  );

  // Handler for closing tracking link modal
  const handleCloseTrackingLinkModal = useCallback(() => {
    closeTrackingLinkModal();
    trackingLinkForm.reset();
    setLoadingTrackingLinkSubmit(false);
  }, [closeTrackingLinkModal, trackingLinkForm]);

  // Convert selected vehicle to Unit format for the modal, or use all vehicles if none selected
  const trackingLinkUnits = useMemo(() => {
    if (trackingLinkSelectedVehicle) {
      return [
        {
          id: trackingLinkSelectedVehicle.id,
          name:
            trackingLinkSelectedVehicle.vehicle?.name ||
            trackingLinkSelectedVehicle.imei ||
            trackingLinkSelectedVehicle.id,
          plate: trackingLinkSelectedVehicle.vehicle?.plates || "",
          type: trackingLinkSelectedVehicle.vehicle?.type || "car",
          createdAt: { _seconds: 0, _nanoseconds: 0 },
          updatedAt: { _seconds: 0, _nanoseconds: 0 },
          collections: [],
          devices: [],
        },
      ];
    }
    // When no vehicle is preselected, use all available vehicles
    return allVehicles.map((vehicle) => ({
      id: vehicle.id,
      name: vehicle.vehicle?.name || vehicle.imei || vehicle.id,
      plate: vehicle.vehicle?.plates || "",
      type: vehicle.vehicle?.type || "car",
      createdAt: { _seconds: 0, _nanoseconds: 0 },
      updatedAt: { _seconds: 0, _nanoseconds: 0 },
      collections: [],
      devices: [],
    }));
  }, [trackingLinkSelectedVehicle, allVehicles]);

  const toggleGrid = () => setShowGrid(!showGrid);

  useVehiclesSubscription();

  // Theme classes para modales
  const modalThemeClasses: ThemeClasses = useMemo(
    () => ({
      bg: isDarkMode ? "bg-dark-surface" : "bg-white",
      border: isDarkMode ? "border-dark-border" : "border-gray-200",
      text: isDarkMode ? "text-white" : "text-gray-900",
      textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
      cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
      cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
      buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
      buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
      buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
    }),
    [isDarkMode],
  );

  // Hook para formulario de POI
  const {
    form: poiForm,
    mapCenter: poiMapCenter,
    markerPosition: poiMarkerPosition,
    hasRealChanges: poiHasRealChanges,
    handleMarkerPositionChange: handlePOIMarkerChange,
    handleCoordinateChange: handlePOICoordinateChange,
    resetForm: resetPOIForm,
    preparePOIData,
  } = usePointOfInterestForm({ isEdit: false, editPOI: null });

  // Hook para formulario de Geocerca
  const {
    form: geofenceForm,
    shape: geofenceShape,
    polygonPoints,
    setPolygonPoints,
    linearPoints,
    setLinearPoints,
    routeDistance,
    estimatedTime,
    polygonArea,
    hasRealChanges: geofenceHasRealChanges,
    handleAddressSelect: handleGeofenceAddressSelect,
    handleLinearPointsChange,
    handleAxisCalculated,
    handleBufferChange,
    linearAddresses,
    handleLinearAddressEdit,
    handleRouteCalculated,
    resetForm: resetGeofenceForm,
    prepareGeofenceData,
    defaultCircularCoordinates,
  } = useGeofenceForm({ isEdit: false, editGeofence: null });

  // Funciones para manejar modales
  const handleOpenPOIModal = useCallback(() => {
    setShowPOIModal(true);
  }, []);

  const handleClosePOIModal = useCallback(() => {
    setShowPOIModal(false);
    setLoadingPOISubmit(false);
    resetPOIForm();
  }, [resetPOIForm]);

  const handleOpenGeofenceModal = useCallback(() => {
    setShowGeofenceModal(true);
  }, []);

  const handleCloseGeofenceModal = useCallback(() => {
    setShowGeofenceModal(false);
    setLoadingGeofenceSubmit(false);
    resetGeofenceForm();
    resetLastProcessedCoordinates();
  }, [resetGeofenceForm]);

  // Handler para crear POI
  const handleCreatePOI = useCallback(
    async (data: any) => {
      setLoadingPOISubmit(true);
      // Obtener userId del usuario o del localStorage como fallback
      const userId = user?.id || localStorage.getItem("userId") || "";
      const poiData = {
        ...preparePOIData(data),
        createdBy: userId,
      };

      const result = await createPointOfInterest(poiData);

      if (result.success) {
        // Refetch para actualizar la lista de puntos de interés
        fetchPointsOfInterest({ limit: 100, page: 1 });
        queryClient.invalidateQueries({ queryKey: ["pointsInterest"] });

        showAlert({
          title: t("general.success", { ns: "common" }),
          text: t("poi.createSuccess", {
            ns: "common",
            defaultValue: "Punto de interés creado exitosamente",
          }),
          icon: "success",
          confirmButtonText: t("general.accept", { ns: "common" }),
          confirmButtonColor: brand.primary,
        });
        handleClosePOIModal();
      } else {
        showAlert({
          title: t("general.error", { ns: "common" }),
          text:
            result.error ||
            t("poi.createError", {
              ns: "common",
              defaultValue: "Error al crear el punto de interés",
            }),
          icon: "error",
          confirmButtonText: t("general.accept", { ns: "common" }),
          confirmButtonColor: brand.primary,
        });
      }

      setLoadingPOISubmit(false);
    },
    [
      preparePOIData,
      createPointOfInterest,
      showAlert,
      t,
      handleClosePOIModal,
      user,
      queryClient,
    ],
  );

  // Handler para crear Geocerca
  const handleCreateGeofence = useCallback(
    async (data: any) => {
      setLoadingGeofenceSubmit(true);
      const geofenceData = prepareGeofenceData(data);

      await createGeofence(
        geofenceData,
        showAlert,
        setLoadingGeofenceSubmit,
        t,
        () => {
          handleCloseGeofenceModal();
        },
        geofenceForm.reset,
      );

      useGeofencesStore.getState().fetchGeofences({ limit: 1000, page: 1 });
      queryClient.invalidateQueries({ queryKey: ["geofences"] });
    },
    [
      prepareGeofenceData,
      showAlert,
      t,
      handleCloseGeofenceModal,
      geofenceForm.reset,
      queryClient,
    ],
  );

  // Set default map type
  useEffect(() => {
    if (!mapType) {
      setMapType("roadmap");
    }
  }, [mapType]);

  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);
  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "lite") {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("common:appsSidebar.comingSoon"),
        text: t("common:appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("common:appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("common:appsSidebar.premiumFeature"),
        text: t("common:appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("common:appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      // La cortina de cambio de app la pone AppsSidebar (barra y menú).
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    if (user?.id) {
      requestFCMToken({ id: user.id });
    }
  }, [user?.id]);

  // Sincronizar companyUser y companyId desde localStorage al cargar
  useEffect(() => {
    const storedCompanyUser = localStorage.getItem("userCompanyId");
    if (storedCompanyUser && storedCompanyUser !== companyUser) {
      setCompanyUser(storedCompanyUser);
    }

    const storedCompanyId = localStorage.getItem("companyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    } else if (!storedCompanyId && storedCompanyUser) {
      // Si no tiene companyId en localStorage y no es admin_reseller, usar su propia compañía
      const roleId = localStorage.getItem("userRoleId") || "";
      if (roleId !== ADMIN_RESELLER_ROLE_ID) {
        setCompanyId(storedCompanyUser);
      }
    }
  }, [companyUser, setCompanyUser, companyId, setCompanyId]);

  // Cargar compañías si es reseller
  useEffect(() => {
    if (isReseller) {
      fetchAllCompanies();
    }
  }, [isReseller, fetchAllCompanies]);

  const activeCompanyId =
    selectedCompany || companyId || localStorage.getItem("companyId") || "";
  // Carga vía TanStack con write-through al store (consumido por VehicleCard,
  // VehicleEvidenceModal, etc.). No toca el evidenceSocket.
  useCompanyIntegrators(activeCompanyId || null);

  return (
    <LayoutContext.Provider
      value={{
        showGrid,
        toggleGrid,
        mapType,
        setMapType,
        showTraffic,
        setShowTraffic,
        showTransit,
        setShowTransit,
        showBicycling,
        setShowBicycling,
        showGeofences,
        setShowGeofences,
        geofenceFilter,
        setGeofenceFilter,
        showInterestPoints,
        setShowInterestPoints,
        showRoutes,
        setShowRoutes,
        showRuler,
        setShowRuler,
        showIcons,
        setShowIcons,
        showVehicleLabels,
        setShowVehicleLabels,
        showSearchBox,
        setShowSearchBox,
      }}
    >
      <div
        className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? "bg-black" : "bg-gray-50"}`}
      >
        {/* Banner de sesión por expirar */}
        {isExpiringSoon && (
          <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
            <i className="ri-alarm-warning-line"></i>
            <span>
              {t("common:sessionBanner.expiring", {
                time: timeRemainingFormatted,
              })}
            </span>
          </div>
        )}

        <Header
          isDarkMode={isDarkMode}
          onOpenAppsSidebar={openAppsSidebar}
          isMobile={isMobile}
          isReseller={isReseller}
          companies={isReseller ? allCompanies : companies}
          selectedCompany={selectedCompany}
          onCompanyChange={setSelectedCompany}
          // El buscador de unidades vive en el panel lateral (UnitSearch).
        />
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Desktop Sidebar - Hidden on mobile */}
          {!isMobile && (
            <Sidebar
              isDarkMode={isDarkMode}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={toggleSidebar}
            />
          )}
          {/* relative: la vista de manejo (DriveViewHost) flota aquí encima del mapa. */}
          <div data-map-area className={`relative flex-1 min-h-0 overflow-hidden min-w-0 w-full ${isMobile ? "pb-[var(--lite-bottom-bar-h,5rem)]" : ""}`}>
            {/* Controles del mapa: píldoras sobre el mapa (antes, ojo y herramienta del encabezado). */}
            <MapControlsBar
              isDarkMode={isDarkMode}
              isReseller={isReseller}
              selectedCompany={selectedCompany}
              controls={{
                mapType: mapType || "roadmap",
                setMapType,
                showTraffic,
                setShowTraffic,
                showTransit,
                setShowTransit,
                showBicycling,
                setShowBicycling,
                showGeofences,
                setShowGeofences,
                showInterestPoints,
                setShowInterestPoints,
                showRoutes,
                setShowRoutes,
                showRuler,
                setShowRuler,
                showIcons,
                setShowIcons,
                showVehicleLabels,
                setShowVehicleLabels,
                showSearchBox,
                showGrid,
                onToggleGrid: toggleGrid,
                handleRulerToggle: () => {
                  const newRulerState = !showRuler;
                  setShowRuler(newRulerState);
                  if (newRulerState && showSearchBox) {
                    setShowSearchBox(false);
                  }
                },
                handleSearchBoxToggle: () => {
                  const newSearchBoxState = !showSearchBox;
                  setShowSearchBox(newSearchBoxState);
                  if (newSearchBoxState && showRuler) {
                    setShowRuler(false);
                  }
                },
                onOpenPOIModal: handleOpenPOIModal,
                onOpenGeofenceModal: handleOpenGeofenceModal,
                onOpenTrackingLinkModal: openTrackingLinkModal,
              }}
            />
            <UnitsTableDock isDarkMode={isDarkMode} enabled={!isMobile}>
              <Outlet
                context={{
                  showGrid,
                  mapType,
                  setMapType,
                  showTraffic,
                  showTransit,
                  showBicycling,
                  showGeofences,
                  setShowGeofences,
                  geofenceFilter,
                  setGeofenceFilter,
                  showInterestPoints,
                  setShowInterestPoints,
                  showRoutes,
                  setShowRoutes,
                  showRuler,
                  setShowRuler,
                  showIcons,
                  setShowIcons,
                  showVehicleLabels,
                  setShowVehicleLabels,
                  showSearchBox,
                  setShowSearchBox,
                }}
              />
            </UnitsTableDock>
            <DriveViewHost isDarkMode={isDarkMode} />
          </div>
        </div>

        {/* Mobile Bottom Bar - Only visible on mobile */}
        {isMobile && (
          <MobileBottomBar
            isDarkMode={isDarkMode}
            unitStats={unitStats}
            vehicles={vehicles}
            selectedStatus={selectedStatus}
            onStatusClick={handleStatusClick}
            setSelectedStatus={setSelectedStatus}
            groups={groups}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            isReseller={isReseller}
            companies={isReseller ? allCompanies : companies}
            selectedCompany={selectedCompany}
            onCompanyChange={setSelectedCompany}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearchClear={clearSearch}
            searchPlaceholder={t("common:header.searchPlaceholder")}
            searchResultCount={vehicles.length}
            searchTotalCount={allVehicles.length}
            loading={vehiclesLoading && !awaitingCompanySelection}
            isConnected={isConnected}
            themeClasses={mobileThemeClasses}
          />
        )}

        <AppsSidebar
          isOpen={isAppsSidebarOpen}
          onClose={closeAppsSidebar}
          categories={filteredCategories}
          selectedAppId="lite"
          onAppSelect={handleAppSelect}
          onStoreClick={() => navigate("/store")}
          isDarkMode={isDarkMode}
          currentTheme={themeMode}
          onThemeChange={setTheme}
          userInfo={
            user
              ? {
                  first_name: user.first_name,
                  last_name: user.last_name,
                  email: user.email,
                  username: user.username,
                }
              : undefined
          }
          onProfileClick={() => {
            closeAppsSidebar();
            navigate("/profile");
          }}
          onLogout={async () => {
            setIsLoggingOut(true);
            await logoutUser();
            setIsLoggingOut(false);
          }}
          isLoggingOut={isLoggingOut}
        />

        {/* Modal de Punto de Interés */}
        <PointOfInterestModal
          isOpen={showPOIModal}
          isEdit={false}
          loadingSubmit={loadingPOISubmit}
          form={poiForm}
          themeClasses={modalThemeClasses}
          isDarkMode={isDarkMode}
          mapCenter={poiMapCenter}
          markerPosition={poiMarkerPosition}
          hasRealChanges={poiHasRealChanges}
          onMarkerPositionChange={handlePOIMarkerChange}
          onCoordinateChange={handlePOICoordinateChange}
          onSubmit={handleCreatePOI}
          onClose={handleClosePOIModal}
        />

        {/* Modal de Geocerca */}
        <GeofenceModal
          isOpen={showGeofenceModal}
          isEdit={false}
          loadingSubmit={loadingGeofenceSubmit}
          form={geofenceForm}
          shape={geofenceShape}
          themeClasses={modalThemeClasses}
          isDarkMode={isDarkMode}
          editGeofence={null}
          polygonPoints={polygonPoints}
          setPolygonPoints={setPolygonPoints}
          linearPoints={linearPoints}
          setLinearPoints={setLinearPoints}
          routeDistance={routeDistance}
          estimatedTime={estimatedTime}
          polygonArea={polygonArea}
          defaultCircularCoordinates={defaultCircularCoordinates}
          hasRealChanges={geofenceHasRealChanges}
          onAddressSelect={handleGeofenceAddressSelect}
          onLinearPointsChange={handleLinearPointsChange}
          onRouteCalculated={handleRouteCalculated}
          onAxisCalculated={handleAxisCalculated}
          onBufferChange={handleBufferChange}
          linearAddresses={linearAddresses}
          onLinearAddressEdit={handleLinearAddressEdit}
          onSubmit={handleCreateGeofence}
          onClose={handleCloseGeofenceModal}
        />

        {/* Modal de Link de Seguimiento */}
        <TrackingLinkModal
          isOpen={trackingLinkModalOpen}
          isEdit={false}
          loadingSubmit={loadingTrackingLinkSubmit}
          form={trackingLinkForm}
          themeClasses={modalThemeClasses}
          isDarkMode={isDarkMode}
          units={trackingLinkUnits}
          onSubmit={handleCreateTrackingLink}
          onClose={handleCloseTrackingLinkModal}
        />

        {/* Modal de Detalles de Link de Seguimiento */}
        <TrackingLinkDetailsModal
          isOpen={trackingLinkDetailsOpen}
          onClose={closeTrackingLinkDetailsModal}
          trackingLink={selectedTrackingLink}
          themeClasses={modalThemeClasses}
          isDarkMode={isDarkMode}
        />
      </div>
    </LayoutContext.Provider>
  );
};

export default LiteLayout;
